import classNames from 'classnames';
import { Fragment, FunctionComponent, HTMLAttributes, useContext } from 'react';
import styled from 'styled-components';

import { CanvasDrop } from '@components/canvas';
import { BlockContext } from '@context/blocks';
import { PermissionLevelContext } from '@context/permission-level';
import { PreviewContext } from '@context/preview';
import { BlockTypes } from '@enums/blocks';
import { Block } from '@typings/block';
import { getBlockDataIsSection } from '@utilities/block';

import { BlockRender } from '../block-render';

interface SectionRenderProps extends HTMLAttributes<HTMLDivElement> {
  block: Block;
}

const UnstyledSectionRender: FunctionComponent<SectionRenderProps> = (props) => {
  const { block, ...otherProps } = props;

  const { pageBlocks } = useContext(BlockContext);
  const { editable } = useContext(PermissionLevelContext);
  const { preview } = useContext(PreviewContext);

  const data = getBlockDataIsSection(block.data) ? block.data : null;

  if (!data) return null;

  const editing = editable && !preview;
  const sectionBlocks = pageBlocks.filter((b) => block.key === b.parentKey);

  return (
    <section
      {...otherProps}
      className={classNames({
        section: true,
        'section--editing': editing,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
      data-testid={data.id}
      id={data.id}
    >
      {sectionBlocks.map((b, index) => {
        const { createdAt, deletedAt, position, updatedAt, type } = b;

        if (deletedAt) return null;

        const first = index === 0;
        const last = index === sectionBlocks.length - 1;
        const className = type === BlockTypes.Color ? 'canvas-drop--color' : undefined;
        const key = `${b.key}-${createdAt}-${updatedAt}-${position}-${index}`;

        if (first) {
          return (
            <Fragment key={key}>
              <BlockRender block={b} index={index} section />
              {first && last && <CanvasDrop parentKey={block.key} position={position + 1} />}
            </Fragment>
          );
        }

        return (
          <Fragment key={key}>
            <CanvasDrop className={className} parentKey={block.key} position={position} />
            <BlockRender block={b} index={index} section />
            {last && <CanvasDrop parentKey={block.key} position={position + 1} />}
          </Fragment>
        );
      })}
    </section>
  );
};

const StyledSectionRender = styled(UnstyledSectionRender)``;

export const SectionRender: FunctionComponent<SectionRenderProps> = (props) => {
  return <StyledSectionRender {...props} />;
};
